import { sectionRenderer } from '@theme/section-renderer';
import { initializeTrustpilotWidgets } from '@theme/trustpilot-widgets';

/**
 * Handles the "View All" button click to enable infinite scroll on collection pages
 */
class ViewAllCollection {
  constructor() {
    this.handleViewAllClick = this.handleViewAllClick.bind(this);
    this.init();
  }

  init() {
    // Use event delegation to handle clicks on view-all buttons
    // This works even after section morphing
    document.addEventListener('click', this.handleViewAllClick);
  }

  /**
   * Handles the click event on the view-all button
   * @param {Event} event
   */
  async handleViewAllClick(event) {
    // Check if the clicked element is a view-all button or is inside one
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-view-all-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const resultsList = document.querySelector('results-list');

    if (!resultsList) {
      console.warn('View All: results-list element not found');
      return;
    }

    const sectionId = resultsList.getAttribute('section-id');
    if (!sectionId) {
      console.warn('View All: section-id not found');
      return;
    }

    // Disable the button to prevent multiple clicks
    button.disabled = true;
    button.textContent = button.textContent + '...';
    button.setAttribute('aria-busy', 'true');

    try {
      const gridElement = resultsList.querySelector('[ref="grid"]') || resultsList.querySelector('.product-grid');
      if (!(gridElement instanceof HTMLElement)) {
        console.warn('View All: product grid not found');
        return;
      }

      const lastPage = Number(gridElement.dataset.lastPage || '1');
      if (Number.isNaN(lastPage) || lastPage <= 1) {
        return;
      }

      const lastCard = gridElement.querySelector('[ref="cards[]"]:last-child');
      const currentPage =
        lastCard instanceof HTMLElement ? Number(lastCard.dataset.page || '1') : 1;

      // Remove pagination controls from the DOM
      const paginationNav = button.closest('.pagination');
      if (paginationNav) {
        paginationNav.remove();
      }

      // Update URL to remove page parameter and add view_all parameter
      const baseUrl = new URL(window.location.href);
      baseUrl.searchParams.delete('page');
      baseUrl.searchParams.set('view_all', 'true');
      baseUrl.hash = '';

      // Update browser URL without reloading
      window.history.replaceState({}, '', baseUrl.toString());

      for (let page = currentPage + 1; page <= lastPage; page += 1) {
        const pageUrl = new URL(baseUrl.toString());
        pageUrl.searchParams.set('page', page.toString());

        const pageContent = await sectionRenderer.getSectionHTML(sectionId, false, pageUrl);
        const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
        const newGrid = parsedPage.querySelector('[ref="grid"]');
        if (!newGrid) {
          break;
        }

        const newItems = newGrid.querySelectorAll(':scope > [ref="cards[]"]');
        if (newItems.length === 0) {
          break;
        }

        gridElement.append(...newItems);
      }

      requestAnimationFrame(() => {
        initializeTrustpilotWidgets(gridElement);
      });
    } catch (error) {
      console.error('View All: Error loading all products', error);
      button.disabled = false;
      // Restore original text
      const originalText = button.getAttribute('aria-label') || 'View all';
      button.textContent = originalText;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ViewAllCollection();
  });
} else {
  new ViewAllCollection();
}

